// src/pages/gro/GroSettingsPage.tsx
import { useEffect, useState } from "react";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/shared/PageHeader";
import { GroSettingsForm } from "@/components/gro/GroSettingsForm";
import { useGroSettings, useUpdateGroSettings } from "@/hooks/useGroSettings";
import { useAuth } from "@/contexts/AuthContext";
import { GRO_IMPORT_SUMMARY_LABELS, GRO_MODEL_IMPORT_ITEMS } from "@/lib/groModelImport";
import { PERMISSIONS } from "@/lib/permissions";
import type { GroSettingsUpdateIn } from "@/types/gro";
import { useImportGroModel } from "@/hooks/useModelImports";
import { useSelectedClientCompany } from "@/hooks/useSelectedClientCompany";

const emptyForm: GroSettingsUpdateIn = {
  risk_suggestion_mode: "suggest_only",
  action_plan_suggestion_mode: "suggest_only",
  inventory_mode: "continuous",
  allow_unit_analysis: true,
  allow_department_analysis: true,
  allow_job_position_analysis: true,
  allow_worker_group_analysis: true,
  min_respondents_for_aggregation: 5,
};

export default function GroSettingsPage() {
  const { user } = useAuth();
  const canWrite = user?.permissions?.includes(PERMISSIONS.GRO_SETTINGS_WRITE) ?? false;

  const settingsQ = useGroSettings();
  const updateMut = useUpdateGroSettings();
  const importGroMut = useImportGroModel();
  const { selectedClientCompanyId } = useSelectedClientCompany();

  const [form, setForm] = useState<GroSettingsUpdateIn>(emptyForm);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);

  useEffect(() => {
    if (settingsQ.data) {
      setForm({
        risk_suggestion_mode: settingsQ.data.risk_suggestion_mode,
        action_plan_suggestion_mode: settingsQ.data.action_plan_suggestion_mode ?? "suggest_only",
        inventory_mode: settingsQ.data.inventory_mode,
        allow_unit_analysis: settingsQ.data.allow_unit_analysis,
        allow_department_analysis: settingsQ.data.allow_department_analysis,
        allow_job_position_analysis: settingsQ.data.allow_job_position_analysis,
        allow_worker_group_analysis: settingsQ.data.allow_worker_group_analysis,
        min_respondents_for_aggregation:
          settingsQ.data.min_respondents_for_aggregation,
      });
    }
  }, [settingsQ.data]);

  async function handleImportGroModel() {
    if (!selectedClientCompanyId) {
      toast.error("Selecione um Cliente para importar a Estrutura GRO da Empresa.");
      return;
    }

    try {
      const summary = await importGroMut.mutateAsync();
      const imported = Object.values(summary.imported || {}).reduce((acc, value) => acc + Number(value || 0), 0);
      const skipped = Object.values(summary.skipped || {}).reduce((acc, value) => acc + Number(value || 0), 0);
      const details = Object.entries(summary.imported || {})
        .filter(([, value]) => Number(value || 0) > 0)
        .map(([key, value]) => `${GRO_IMPORT_SUMMARY_LABELS[key] ?? key}: ${value}`)
        .join(" · ");
      toast.success(`Estrutura GRO importada: ${imported} importado(s), ${skipped} ignorado(s).${details ? ` ${details}` : ""}`);
      setConfirmImportOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao importar a Estrutura GRO da Empresa.");
    }
  }

  async function handleSave() {
    if (form.min_respondents_for_aggregation < 1) {
      toast.error("O mínimo de respondentes deve ser maior que zero.");
      return;
    }

    try {
      await updateMut.mutateAsync(form);
      toast.success("Configurações salvas com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar configurações.");
    }
  }

  return (
    <div>
      <PageHeader
        title="Configurações"
        description="Defina como o módulo psicossocial e de gestão de riscos deve funcionar."
      />

      <Card className="mb-6 border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Importar Estrutura GRO da Empresa</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            A importação é opcional, copia os modelos para o Cliente selecionado e não sobrescreve itens já personalizados. Duplicados serão ignorados.
          </p>
          <div className="grid gap-2 md:grid-cols-2">
            {GRO_MODEL_IMPORT_ITEMS.map((item) => (
              <div key={item} className="flex items-center gap-2 rounded-lg border bg-background/80 px-3 py-2 text-sm">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => setConfirmImportOpen(true)}
              disabled={!canWrite || !selectedClientCompanyId || importGroMut.isPending}
            >
              {importGroMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Importar Estrutura GRO
            </Button>
          </div>
        </CardContent>
      </Card>

      {settingsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando configurações...
        </Card>
      ) : settingsQ.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(settingsQ.error as any)?.message || "Erro ao carregar configurações"}
          </div>
        </Card>
      ) : (
        <GroSettingsForm
          value={form}
          readOnly={!canWrite}
          saving={updateMut.isPending}
          onChange={setForm}
          onSubmit={handleSave}
        />
      )}

      <Dialog open={confirmImportOpen} onOpenChange={setConfirmImportOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirmar importação da Estrutura GRO</DialogTitle>
            <DialogDescription>
              Todos os itens abaixo serão copiados da Empresa para o Cliente selecionado. Registros já existentes serão reutilizados e não serão duplicados.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 md:grid-cols-2">
            {GRO_MODEL_IMPORT_ITEMS.map((item) => (
              <div key={item} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmImportOpen(false)}>Cancelar</Button>
            <Button onClick={handleImportGroModel} disabled={importGroMut.isPending}>
              {importGroMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Confirmar importação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
